import { citationIdentity } from "../../../domains/gpt/citation";
import { useTranslation } from "react-i18next";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { cn } from "../../../lib/utils";
import { SourceSignals } from "../../ui/SourceSignals";
import { SourceMethodBadge } from "./SourceMethodReadout";
import { useApiQuery } from "../../../services/api";
import {
  CITATION_BADGE_CLASS,
  evidenceSourceOrder,
  verbatimQuotesBySource,
} from "./EvidenceAnswer";
import {
  type EvidenceGraphView,
  type EvidenceSourceView,
} from "../../../domains/gpt/hooks/useEvidenceGptChat";

// How many sources an Evidence turn's Sources tab lists — the full shared
// numbering (prose-cited first, then edge-only), so the tab's pill count and
// the rendered rows always agree.
export function evidenceSourceCount(answer: EvidenceGraphView): number {
  return evidenceSourceOrder(answer).length;
}

/**
 * One Evidence turn's numbered source list: for each cited source its number
 * badge, title, citation identity ("Raghu G et al. · 2022"), journal, and the
 * Confidence/Endorsement signals. Each row opens the citation sheet with the
 * source's gate-verified quotes. Rendered in the Sources tab on the unified
 * surface and below the answer on the legacy standalone Evidence surface;
 * numbering matches the answer's inline [n] chips (`evidenceSourceOrder`).
 */
export function EvidenceSourcesList({
  answer,
  onOpenSource,
}: {
  answer: EvidenceGraphView;
  onOpenSource: (
    source: EvidenceSourceView,
    citationNumber: number,
    quotes: readonly string[]
  ) => void;
}) {
  const { t } = useTranslation();
  // The Work's methodological identity for each cited source, read live (gated
  // behind the experimental flag). Powers the answer-level coverage marker and
  // the per-source method chip. Called before any early return so hook order is
  // stable.
  const methodEnabled = useFeatureIsOn("METHOD_READOUT");
  const workIds = [
    ...new Set(
      answer.sources.flatMap((source) =>
        source.workId === null ? [] : [source.workId]
      )
    ),
  ];
  const methodQuery = useApiQuery(
    "/webapp/private/works/risk-of-bias",
    methodEnabled && workIds.length > 0
      ? { query: { ids: workIds.join(",") }, durable: true }
      : false
  );
  const methodByWorkId = new Map(
    methodQuery.state === "success"
      ? methodQuery.body.map((row) => [row.workId, row.method])
      : []
  );
  const order = evidenceSourceOrder(answer);
  const sourceById = new Map(
    answer.sources.map((source) => [source.id, source])
  );
  const quotesBySourceId = verbatimQuotesBySource(answer.edges);

  // Nothing cited → render nothing: the answer body already carries the
  // ungrounded message (stacking it twice on the standalone surface), and the
  // unified Sources tab is disabled at a zero count anyway.
  if (order.length === 0) return null;

  // Coverage for the header marker: a cited source counts as analysed once it has
  // a settled method result (appraised OR "no grid applies"); pending, failed, or
  // no-Work sources are not analysed — we never claim to have looked at what we
  // did not.
  const analysedCount = order.filter((id) => {
    const source = sourceById.get(id);
    if (source === undefined || source.workId === null) return false;
    const method = methodByWorkId.get(source.workId);
    return method !== undefined && method !== null;
  }).length;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-ds-slate-600">
        {/* Only claim coverage once the read has SETTLED and at least one source is
            actually analysed. A loading/idle read, or an answer whose sources are
            all workId-null (historical backfill, uncrawled web sources), would
            otherwise show a misleading "0 of N analysed" — so those fall back to the
            neutral source count. */}
        {methodEnabled && methodQuery.state === "success" && analysedCount > 0
          ? t("{{analyzed}} of {{total}} sources analysed", {
              analyzed: analysedCount,
              total: order.length,
            })
          : t("{{count}} sources", { count: order.length })}
      </span>
      {order.map((id, index) => {
        const source = sourceById.get(id);
        if (source === undefined) return null;
        const methodForSource =
          source.workId === null ? null : methodByWorkId.get(source.workId);
        return (
          <button
            key={id}
            type="button"
            onClick={() => {
              const quotes = quotesBySourceId.get(source.id);
              onOpenSource(
                source,
                index + 1,
                quotes === undefined ? [] : quotes
              );
            }}
            className={cn(
              "flex items-start gap-2 rounded-xl px-2 py-1.5 text-left",
              "hover:bg-ds-slate-50"
            )}
          >
            <span className={cn(CITATION_BADGE_CLASS, "mt-0.5")}>
              {index + 1}
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-sm/snug text-ds-black">
                {source.title === null ? t("Untitled source") : source.title}
              </span>
              {citationIdentity(source) !== "" && (
                <span className="text-xs text-ds-slate-600">
                  {citationIdentity(source)}
                </span>
              )}
              {source.journal !== null && (
                <span className="text-xs text-ds-mint-800">
                  {source.journal}
                </span>
              )}
              <SourceSignals
                quality={source.quality}
                endorsement={source.endorsement}
              />
              {methodForSource !== undefined && methodForSource !== null && (
                <SourceMethodBadge method={methodForSource} />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
